import type { DashboardLayoutItem } from "../../../types";

export const GRID_COLUMNS = 12;
/** Height of one grid row in px. Rows are small so a widget's height can closely match its content. */
export const GRID_ROW_HEIGHT = 4;
/** Gap between widgets in px (both axes). Vertically it is carved out of the bottom of each item's own rows. */
export const GRID_MARGIN = 24;
export const DASHBOARD_LAYOUT_VERSION = 1;

export function sortByReadingOrder(items: DashboardLayoutItem[]): DashboardLayoutItem[] {
  return [...items].sort((a, b) => a.y - b.y || a.x - b.x);
}

function overlaps(a: DashboardLayoutItem, b: DashboardLayoutItem): boolean {
  return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}

export function autoLayout(items: DashboardLayoutItem[], columns: number = GRID_COLUMNS): DashboardLayoutItem[] {
  const placed: DashboardLayoutItem[] = [];

  for (const item of sortByReadingOrder(items)) {
    const w = Math.min(item.w, columns);
    let found: DashboardLayoutItem | null = null;

    for (let y = 0; found === null; y++) {
      for (let x = 0; x <= columns - w; x++) {
        const candidate = { ...item, w, x, y };
        if (!placed.some((p) => overlaps(candidate, p))) {
          found = candidate;
          break;
        }
      }
    }

    placed.push(found);
  }

  return placed;
}

/**
 * Moves an item one step earlier (-1) or later (1) in reading order by swapping places with its neighbour.
 * Same-size neighbours just trade positions; otherwise the swapped layout is repacked with autoLayout.
 */
export function moveItem(items: DashboardLayoutItem[], i: string, direction: -1 | 1): DashboardLayoutItem[] {
  const ordered = sortByReadingOrder(items);
  const index = ordered.findIndex((item) => item.i === i);
  const neighbour = ordered[index + direction];
  if (index === -1 || !neighbour) return items;

  const item = ordered[index];
  const swapped = items.map((current) => {
    if (current.i === item.i) return { ...current, x: neighbour.x, y: neighbour.y };
    if (current.i === neighbour.i) return { ...current, x: item.x, y: item.y };
    return current;
  });

  return item.w === neighbour.w && item.h === neighbour.h ? swapped : autoLayout(swapped);
}

export function appendItem(
  items: DashboardLayoutItem[],
  item: Omit<DashboardLayoutItem, "x" | "y">,
): DashboardLayoutItem[] {
  const bottom = items.reduce((max, n) => Math.max(max, n.y + n.h), 0);
  return [...items, { ...item, x: 0, y: bottom }];
}

export function resolveLayoutItems(items: DashboardLayoutItem[], knownTypes: string[]): DashboardLayoutItem[] {
  return items.filter((item) => knownTypes.includes(item.type));
}

export function hasAccess(required: string[], granted: string[], isOwner: boolean): boolean {
  if (isOwner) return true;
  return required.every((scope) => granted.includes(scope));
}

export function buildDefaultLayout(
  template: Omit<DashboardLayoutItem, "i">[],
  canUse: (type: string) => boolean,
): DashboardLayoutItem[] {
  return autoLayout(template.filter((t) => canUse(t.type)).map((t) => ({ ...t, i: t.type })));
}

export function generateItemId(): string {
  // crypto.randomUUID only exists in secure contexts (not on plain-http dev hosts).
  const uuid = globalThis.crypto?.randomUUID?.();
  return uuid ?? `w-${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
}
